import { keepCaptionsVisibility } from '@src/services/karaoke/captionsVisibility';
import {
  DEFAULT_KARAOKE_CONFIG,
  DEFAULT_TEXT_TRANSCRIPT_CONFIG,
} from '@src/services/karaoke/karaokeConstants';
import _isEqual from 'lodash/isEqual';
import { makeAutoObservable } from 'mobx';

import { getCaptionFontCorrection } from '../../editor/services/captionFontSubstitution';
import { isLineBreakStyle } from '../../editor/services/captionLineBreakStyle';
import { captionLineBreakChanged } from '../../editor/services/karaokeSegmentation';
import {
  GLOBAL_KARAOKE_PRESETS_QUERY,
  KARAOKE_TEMPLATE_LANGUAGES_QUERY,
} from '../../gql/branding-gql';
import { backgroundVideoSaveService } from '../../services/BackgroundVideoSaveService';
import {
  captionVariantMatchesLanguage,
  findKaraokeLanguageVariant,
  findKaraokeNeutralFamilyMember,
} from '../../services/karaoke/karaokeLanguageVariants';
import {
  type IKaraokePresetProvider,
  KaraokePresetProvider,
} from '../../services/karaoke/KaraokePresetProvider';
import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import {
  DesignCard,
  GenericPresetTemplates,
  KaraokePresetTemplates,
  MediaCard,
  SidebarOption,
} from '../../types.ts/general';
import {
  resolveCaptionLanguageKey,
  resolveSourceLanguageKey,
} from '../../utility/captionLanguage';
import { datoColorToRgba } from '../../utility/color';
import { extractErrorInfo } from '../../utility/error';
import { LogManager } from '../../utility/Logger';
import parsePercent from '../../utility/parsePercent';
import { DEFAULT_TEXT_CONFIG } from './TextProducer';

import type { ElementState } from '../../renderer/ElementState';
import type {
  KaraokeTemplate,
  KaraokeTemplateMetadata,
  LowerThirdTemplate,
  Preset,
  Template,
} from '../../services/karaoke/types';
import type VideoCreatorStore from '../../stores/VideoCreatorStore';
import type {
  DatoColor,
  ExtraElementData,
  GenericTextBranding,
  KaraokeTextBranding,
  LowerThirdTextBranding,
  Showcase,
} from '../../types.ts/story';
import type { AspectRatio } from '../../types.ts/video';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeConfig as KaraokeConfigWithId } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

// Re-export types for backward compatibility
export type { KaraokeTemplate, LowerThirdTemplate, Preset, Template };

type GenericBrandingKeys =
  | 'title'
  | 'font_family'
  | 'font_size'
  | 'fill_color'
  | 'background_color'
  | 'background_transparency'
  | 'font_weight'
  | 'x_alignment'
  | 'italic'
  | 'uppercase'
  | 'aspect_ratio'
  | 'shadow_color'
  | 'shadow_blur'
  | 'shadow_x_offset'
  | 'shadow_y_offset';

type LowerThirdBrandingKeys =
  | GenericBrandingKeys
  | 'enter_animation'
  | 'exit_animation'
  | 'enter_animation_length'
  | 'exit_animation_length'
  | 'x_pos'
  | 'y_pos';

type KaraokeBrandingKeys =
  | GenericBrandingKeys
  | 'animation'
  | 'x_pos'
  | 'y_pos'
  | 'instagram_lines'
  | 'stroke_width'
  | 'stroke_color'
  | 'hide_comma'
  | 'hide_period'
  | 'hide_fillers'
  | 'transcript_effect'
  | 'transcript_split'
  | 'transcript_placement'
  | 'transcript_maximum_length'
  | 'line_break_style'
  | 'transcript_color'
  | 'referencing_showcase';

type BrandingKeys = LowerThirdBrandingKeys | KaraokeBrandingKeys;

const COLOR_BRANDING = [
  'fillColor',
  'backgroundColor',
  'strokeColor',
  'shadowColor',
  'transcriptColor',
];

const BACKGROUND_COLORS = ['backgroundColor'];

const ALL_PRESET_TEMPLATES = [
  ...Object.values(GenericPresetTemplates),
  ...Object.values(KaraokePresetTemplates),
] as string[];

/**
 * PLAT-11653 — a template record a `save()` attempt already wrote to
 * DatoCMS. A failure hands it back so a retry can write into that record
 * instead of creating a second one: `save()` commits the record well before
 * it links the template to the organization and saves the story, so a
 * failure in either of those leaves a record behind that a plain retry would
 * duplicate.
 */
export type CreatedTemplateRecord = {
  templateId: string;
  /** The record's family key, for a language variant. */
  presetId?: string;
};

export type TemplateSaveResult =
  | { ok: true }
  | { ok: false; created?: CreatedTemplateRecord };

/**
 * PLAT-11702 follow-up — one row of the Translation Review's Captions
 * dropdown: a caption template of the showcase's own, ready to apply, plus
 * the name to list it under and the face to draw that name in (brand faces
 * are registered by postScript name, so `fontFamily` is usable as CSS once
 * `useLoadBrandKitFonts` has run on the surface).
 */
/**
 * The names Creator Studio shows for the caption styles that ship with the
 * product, so the review can name one rather than calling it "Clip's Style".
 * `default` is the built-in first-produce look the ticket calls "Impact".
 */
const BUILT_IN_KARAOKE_TEMPLATE_TITLES: Record<string, string> = {
  [GenericPresetTemplates.default]: 'Impact',
  [KaraokePresetTemplates.edge]: 'Edge',
  [KaraokePresetTemplates.classic]: 'Classic',
  [KaraokePresetTemplates.typewriter]: 'Typewriter',
  [KaraokePresetTemplates.blackAndWhite]: 'Black and White',
};

export type CustomKaraokeTemplateOption = {
  config: KaraokeConfig;
  templateId: string;
  title: string;
  fontFamily: string;
  fontWeight: string;
  italic: boolean;
};

export class TextBrandingService {
  private readonly logger = LogManager.getLogger('TextBrandingService');

  videoEditor: IVideoEditor;
  videoCreator: VideoCreatorStore;
  datoClientStore: DatoClientStore;
  templateName: string | undefined = undefined;
  isLoading: boolean = false;
  brandingTemplates: Template[] = [];
  loadedTemplateKeys: Set<string> = new Set();
  globalKaraokePresets: Preset[] = [];
  /**
   * The unmapped DatoCMS records behind `globalKaraokePresets`. Kept because the
   * mapped copies are display data — `getBrandingProps` stringifies their colors
   * and drops `backgroundTransparency` — while anything persisted to or
   * re-applied from a video template needs the DatoCMS shape.
   *
   * Also the one field carrying fetch state: `null` until the request comes back,
   * `[]` once it has and there were none. An environment with no global presets
   * is a legitimate answer, so a count can't stand in for "still in flight".
   */
  globalKaraokePresetRecords: KaraokeTextBranding[] | null = null;

  /**
   * PLAT-11416 — whether this DatoCMS environment has the caption-template
   * `language` field (PLAT-11411 migration). `null` until probed by
   * `loadKaraokeTemplateLanguages`; `false` on an un-migrated environment,
   * where every per-language-variant behavior degrades to Phase 1 and
   * language-tagged writes are skipped (they would be rejected).
   */
  captionTemplateLanguagesSupported: boolean | null = null;
  private karaokeTemplateLanguagesPromise: Promise<void> | null = null;
  /**
   * The aspect ratio the karaoke template list was last built for — lets the
   * language stitch and the language switch rebuild the list (re-filter)
   * without the auto-apply side effects of `getAllBrandingTemplates`.
   */
  private lastKaraokeTemplatesAspectRatio:
    | GenericTextBranding['aspectRatio']
    | null = null;

  selectedBrandingTemplate: Template | undefined = undefined;
  selectedTemplateId: string = GenericPresetTemplates.default;
  lastUsedTemplateId: string = GenericPresetTemplates.default;
  isPropsChanged = false;
  isTemplateModified = false;
  prepareTemplateToSave = false;
  closeActiveState = false;
  isApplyingTemplatePosition = false;
  modifiedTemplateData: {
    template: Template;
    config: ElementState['source'];
  } | null = null;

  constructor(
    videoCreator: VideoCreatorStore,
    datoClientStore: DatoClientStore,
    videoEditor: IVideoEditor,
  ) {
    this.videoCreator = videoCreator;
    this.datoClientStore = datoClientStore;
    this.videoEditor = videoEditor;
    makeAutoObservable(this);
  }

  isTemplateTypeLoaded(
    type: 'basic' | 'karaoke',
    aspectRatio: GenericTextBranding['aspectRatio'],
  ): boolean {
    return this.loadedTemplateKeys.has(`${type}:${aspectRatio}`);
  }

  /**
   * Populates `brandingTemplates` for a type + aspect ratio if it hasn't been.
   * No network: the showcase's brandings arrive with the story and the global
   * presets at startup, so this only maps and filters what is already in memory.
   *
   * For surfaces that need to *list* templates. `getAllBrandingTemplates` is the
   * karaoke sidebar's entry point and does considerably more — it picks a
   * selected template and applies it — so calling that just to read the list
   * rewrites the user's karaoke config as a side effect.
   */
  public ensureTemplatesLoaded(
    type: 'basic' | 'karaoke',
    aspectRatio: GenericTextBranding['aspectRatio'],
  ): void {
    if (this.isTemplateTypeLoaded(type, aspectRatio)) return;
    // Karaoke templates include the global presets, which are fetched during
    // startup. Building before they land would cache a list without them and
    // mark the aspect ratio loaded, so every later caller — the captions sidebar
    // included — would skip its own load and inherit the gap.
    if (type === 'karaoke' && !this.areKaraokePresetsResolved()) return;
    this.loadAllTemplates(type, aspectRatio);
  }

  /**
   * Whether the global presets have been fetched. Customer types opting out of
   * them resolve to an empty list by design, and count as resolved whether or not
   * the request ever ran.
   */
  private areKaraokePresetsResolved(): boolean {
    return (
      this.globalKaraokePresetRecords !== null ||
      !!this.videoCreator.organization?.customerType?.excludeFromGlobalCaptions
    );
  }

  /**
   * Get current organization reference
   */
  public getOrganization(): Showcase {
    return this.videoCreator.organization!;
  }

  getDefaultBasicTextConfig() {
    const aspectRatio =
      this.videoCreator.currentVideo?.aspectRatio ||
      this.videoCreator.getDefaultAspectRatio();
    const basicTextSetting =
      this.videoCreator.textProducer.getBasicTextSettingByAspectRatio(
        aspectRatio,
      );
    return {
      id: GenericPresetTemplates.default,
      title: 'Arbor Default',
      fontFamily: DEFAULT_TEXT_CONFIG.font_family,
      fontSize: basicTextSetting.font_size,
      fillColor: DEFAULT_TEXT_CONFIG.fill_color,
      enterAnimation: 'fade',
      enterAnimationLength: 1,
      exitAnimation: 'fade',
      exitAnimationLength: 1,
      backgroundTransparency: 70,
      backgroundColor: DEFAULT_TEXT_CONFIG.background_color,
      backgroundAlignThreshold: DEFAULT_TEXT_CONFIG.background_align_threshold,
      backgroundBorderRadius: DEFAULT_TEXT_CONFIG.background_border_radius,
      fontWeight: 600,
      yPos: basicTextSetting.y,
      xAlignment: '0%',
      uppercase: false,
      italic: false,
      length: DEFAULT_TEXT_CONFIG.duration,
      aspectRatio,
      type: 'basic',
      shadowColor: DEFAULT_TEXT_CONFIG.shadow_color,
      shadowBlur: DEFAULT_TEXT_CONFIG.shadow_blur,
      shadowXOffset: DEFAULT_TEXT_CONFIG.shadow_x,
      shadowYOffset: DEFAULT_TEXT_CONFIG.shadow_y,
    };
  }

  getDefaultKaraokeTextConfig() {
    const aspectRatio = this.videoCreator.getCurrentAspectRatio();
    const basicTextSetting =
      this.videoCreator.karaokeProducer.getKaraokeTextSettingByAspectRatio(
        aspectRatio,
      );
    // const fontSize = this.videoCreator.textProducer.convertToVh(basicTextSetting.font_size);
    return {
      id: GenericPresetTemplates.default,
      title: 'Impact',
      fontFamily: DEFAULT_KARAOKE_CONFIG.font_family,
      fontSize: basicTextSetting.font_size,
      fillColor: DEFAULT_KARAOKE_CONFIG.fill_color,
      backgroundColor: DEFAULT_KARAOKE_CONFIG.background_color,
      backgroundAlignThreshold:
        DEFAULT_KARAOKE_CONFIG.background_align_threshold,
      backgroundBorderRadius: DEFAULT_KARAOKE_CONFIG.background_border_radius,
      backgroundTransparency: '0.8',
      strokeColor: DEFAULT_KARAOKE_CONFIG.stroke_color,
      strokeWidth: DEFAULT_KARAOKE_CONFIG.stroke_width,
      fontWeight: DEFAULT_KARAOKE_CONFIG.font_weight,
      yPos: basicTextSetting.y,
      xPos: DEFAULT_KARAOKE_CONFIG.x,
      xAlignment: DEFAULT_KARAOKE_CONFIG.x_alignment,
      uppercase: DEFAULT_KARAOKE_CONFIG.text_transform === 'uppercase' || false,
      italic: DEFAULT_KARAOKE_CONFIG.font_style === 'italic' || false,
      instagramLines: DEFAULT_KARAOKE_CONFIG.instagramLines,
      aspectRatio:
        this.videoCreator.currentVideo?.aspectRatio ||
        this.videoCreator.getDefaultAspectRatio(),
      hideComma: DEFAULT_KARAOKE_CONFIG.hideComma,
      hidePeriod: DEFAULT_KARAOKE_CONFIG.hidePeriod,
      hideFillers: DEFAULT_KARAOKE_CONFIG.hideFillers,
      animation: 'none',
      type: 'karaoke',
      shadowColor: DEFAULT_KARAOKE_CONFIG.shadow_color,
      shadowBlur: DEFAULT_KARAOKE_CONFIG.shadow_blur,
      shadowXOffset: DEFAULT_KARAOKE_CONFIG.shadow_x,
      shadowYOffset: DEFAULT_KARAOKE_CONFIG.shadow_y,
      letterSpacing: DEFAULT_KARAOKE_CONFIG.letter_spacing,
    };
  }

  public hasConfigChanged(type: 'basic' | 'karaoke') {
    type Config = ElementState['source'] | KaraokeConfig;
    let initialConfig = {} as Config;
    let config = {} as Config;
    if (type === 'basic') {
      initialConfig = this.videoCreator.textProducer.initializeConfig();
      config = this.videoCreator.textProducer.getTextConfig();
    } else {
      initialConfig = DEFAULT_KARAOKE_CONFIG;
      config = this.getCurrentKaraokeConfig();
    }
    const extranous_fields = this.getExtraneousFields(type);

    const newInitConfig = { ...initialConfig };
    const newConfig = { ...config };

    for (let f of extranous_fields) {
      delete newConfig[f as keyof Config];
      delete newInitConfig[f as keyof Config];
    }

    return !_isEqual(newInitConfig, newConfig);
  }

  public getCurrentKaraokeConfig(): KaraokeConfigWithId {
    const config = this.videoEditor.getDefaultKaraokeConfig();
    if (config == null) {
      throw new Error('Karaoke config is missing for new architecture');
    }
    return config;
  }

  public checkPropsChanged(
    type: 'basic' | 'karaoke',
    propertyMap: ElementState['source'] | KaraokeConfig,
    switched_template = false,
    ignore_keys = false,
  ) {
    const keys = Object.keys(propertyMap);
    const extranous_fields = this.getExtraneousFields(type);

    if (!switched_template) {
      this.templateModified(type, propertyMap, ignore_keys);
    }

    const karaokeAlignmentConditions =
      type === 'karaoke' &&
      keys.length === 2 &&
      keys.includes('x') &&
      keys.includes('x_alignment');
    // The Words Per Line dropdown clears the line-break style while setting a
    // length — one choice, two fields.
    const lineBreakConditions =
      type === 'karaoke' &&
      keys.length === 2 &&
      keys.includes('line_break_style') &&
      keys.includes('transcript_maximum_length');

    if (
      this.isPresetTemplate(this.selectedTemplateId) &&
      this.selectedTemplateId !== GenericPresetTemplates.custom &&
      !switched_template &&
      (keys.length === 1 ||
        karaokeAlignmentConditions ||
        lineBreakConditions ||
        ignore_keys) &&
      (!extranous_fields.includes(keys[0]) || ignore_keys) &&
      !this.prepareTemplateToSave
    ) {
      this.isPropsChanged = true;
    }
  }

  private toggleModifiedTemplateData() {
    this.isTemplateModified = false;
    this.modifiedTemplateData = null;
  }

  private setModifiedTemplateData(type: 'basic' | 'karaoke' = 'basic') {
    const template = this.selectedBrandingTemplate!;

    let config = this.getCurrentKaraokeConfig() as
      | KaraokeConfig
      | ElementState['source'];

    if (type === 'basic') {
      config = this.videoCreator.textProducer.getTextConfig();
      const activeElement = this.videoCreator.getActiveElement();
      config.text = activeElement?.source.text || '';
    }
    this.isTemplateModified = true;
    this.modifiedTemplateData = {
      template,
      config,
    };
  }

  public isPresetTemplate(
    templateId: LowerThirdTextBranding['id'] | KaraokeTextBranding['id'],
  ): boolean {
    if (!templateId) return false;
    return (
      ALL_PRESET_TEMPLATES.includes(templateId) ||
      this.globalKaraokePresets.some(t => t.id === templateId)
    );
  }

  public templateModified(
    type: 'basic' | 'karaoke',
    propertyMap: ElementState['source'] | KaraokeConfig,
    ignore_keys = false,
  ) {
    // Skip modification detection for built-in preset templates
    if (this.isPresetTemplate(this.selectedTemplateId)) {
      return;
    }

    // Skip if we're in the process of preparing to save a new template
    if (this.prepareTemplateToSave) {
      return;
    }

    // Skip if we're applying template positioning (not modifying)
    if (this.isApplyingTemplatePosition) {
      return;
    }

    const keys = Object.keys(propertyMap);
    const extranous_fields = this.getExtraneousFields(type);

    if (ignore_keys) {
      this.setModifiedTemplateData(type);
      return;
    }

    // For basic text templates, check if any non-extraneous field is being modified
    // For karaoke templates, always detect modifications
    const hasValidModification = keys.some(
      key => !extranous_fields.includes(key),
    );

    if (hasValidModification || type === 'karaoke') {
      this.setModifiedTemplateData(type);
    }
  }

  private getExtraneousFields(type: 'basic' | 'karaoke') {
    if (type === 'karaoke') {
      return ['id', 'track', 'type', 'text', 'time', 'duration'];
    }
    return ['id', 'track', 'type', 'text', 'time', 'duration'];
  }

  public getBrandingProps(
    branding: LowerThirdTextBranding | KaraokeTextBranding,
    type: 'basic' | 'karaoke' = 'basic',
  ) {
    const values = {} as Template;
    for (let k in branding) {
      const key = k as keyof (LowerThirdTextBranding | KaraokeTextBranding);
      if (key === 'backgroundTransparency') continue;
      if (!COLOR_BRANDING.includes(key)) {
        values[key] = branding[key] as string;
        continue;
      }

      const color = branding[key] as DatoColor;
      if (!color) {
        values[key] = '';
        continue;
      }

      // A background's opacity comes from the separate `backgroundTransparency`
      // field (0-100) when set, not from the color's own alpha.
      const transparency = BACKGROUND_COLORS.includes(key)
        ? branding.backgroundTransparency
        : undefined;
      values[key] = datoColorToRgba(
        color,
        transparency ? transparency / 100 : undefined,
      );
      values.type = type;
    }
    return values;
  }

  public loadLastUsedTemplate(
    type: 'basic' | 'karaoke' = 'basic',
    aspect_ratio: GenericTextBranding['aspectRatio'] = this.videoCreator.getDefaultAspectRatio(),
  ) {
    if (this.brandingTemplates.length === 0) {
      this.lastUsedTemplateId = GenericPresetTemplates.default;
      return;
    }
    const lastUsedTemplates =
      this.videoCreator.organization?.lastUsedTemplates || [];
    const lastUsedTemplate = lastUsedTemplates.find(
      t => t.type === type && t.aspectRatio === aspect_ratio,
    );
    let isInTemplates = this.brandingTemplates.some(
      t => t.id === lastUsedTemplate?.id,
    );
    if (isInTemplates) {
      this.lastUsedTemplateId = lastUsedTemplate!.id;
    } else {
      const defaultTemplate =
        this.brandingTemplates.find(
          t => t.id === GenericPresetTemplates.default,
        ) || this.brandingTemplates[0];
      this.lastUsedTemplateId = defaultTemplate!.id as string;
    }
  }

  private setLastUsedTemplate(
    templateId: string,
    title: string,
    type: 'basic' | 'karaoke' = 'basic',
    aspect_ratio: GenericTextBranding['aspectRatio'] = this.videoCreator.getDefaultAspectRatio(),
  ) {
    if (!this.videoCreator.organization) return;
    const lastUsedTemplates =
      this.videoCreator.organization?.lastUsedTemplates || [];

    const idx = lastUsedTemplates.findIndex(
      l => l.aspectRatio === aspect_ratio && l.type === type,
    );
    const data = {
      id: templateId,
      type,
      aspectRatio: aspect_ratio,
      title,
    };
    if (idx > -1) {
      lastUsedTemplates[idx] = data;
    } else {
      lastUsedTemplates.push(data);
    }
    this.videoCreator.organization!.lastUsedTemplates = lastUsedTemplates;
    return lastUsedTemplates;
  }

  public async loadKaraokePresets(force = true) {
    // Customer types opting out of global caption presets see an empty list,
    // which falls through to the showcase-owned karaokeTextBranding only.
    if (
      this.videoCreator.organization?.customerType?.excludeFromGlobalCaptions
    ) {
      this.globalKaraokePresets = [];
      // `[]` rather than `null`: resolved, and empty on purpose.
      this.globalKaraokePresetRecords = [];
      return;
    }
    if (!force && this.globalKaraokePresets.length > 0) return;
    try {
      const response = await this.datoClientStore.gqlClient.request<{
        allKaraokeTextBrandings?: KaraokeTextBranding[];
      }>(GLOBAL_KARAOKE_PRESETS_QUERY);

      this.globalKaraokePresetRecords = response.allKaraokeTextBrandings || [];
      this.globalKaraokePresets = (response.allKaraokeTextBrandings || []).map(
        b => ({
          ...this.getBrandingProps(b, 'karaoke'),
          // presetId can be empty for manually added presets (those that are not in
          // KaraokePresetTemplates or GenericPresetTemplates)

          // use titles for old karaoke preset without IDs for consistency.
          // If we use uuid, it will be re-created on every load and we will lose the preset selection info
          id: b.presetId || b.id || `unknown-id-${b.title}`,
          previewText: b.previewText,
        }),
      );
      console.debug(
        'Loaded global karaoke presets:',
        this.globalKaraokePresets.length,
      );
    } catch (err) {
      console.error('Failed to load global karaoke presets:', err);
    }
  }

  /**
   * PLAT-11416 — fetch the org caption templates' `language` values and
   * stitch them onto the in-memory `organization.karaokeTextBranding`
   * records (which the main fragments deliberately load without the field —
   * see KARAOKE_TEMPLATE_LANGUAGES_QUERY). Probes the environment's schema
   * as a side effect: a failure (un-migrated environment) sets
   * `captionTemplateLanguagesSupported = false` and everything degrades to
   * the Phase 1 behavior. Idempotent; fire-and-forget from
   * `loadAllTemplates`.
   */
  loadKaraokeTemplateLanguages(): Promise<void> {
    if (this.captionTemplateLanguagesSupported !== null) {
      return Promise.resolve();
    }
    if (this.karaokeTemplateLanguagesPromise) {
      return this.karaokeTemplateLanguagesPromise;
    }
    const records = this.videoCreator.organization?.karaokeTextBranding;
    if (!records?.length) return Promise.resolve();
    this.karaokeTemplateLanguagesPromise = (async () => {
      try {
        const ids = records.map(r => r.id).filter(Boolean);
        const response = await this.datoClientStore.gqlClient.request<{
          allKaraokeTextBrandings?: Array<{
            id: string;
            language?: string | null;
          }>;
        }>(KARAOKE_TEMPLATE_LANGUAGES_QUERY, { ids });
        const rows = response.allKaraokeTextBrandings || [];
        if (rows.length >= 500) {
          // The query's page cap — languages beyond it never stitch, and
          // untagged variants then pass the picker filter as neutral.
          this.logger.warn(
            'Caption template language query returned a full page — some variants may be unfiltered',
            { count: rows.length },
          );
        }
        const languageById = new Map(
          rows.map(r => [r.id, r.language || undefined]),
        );
        for (const record of records) {
          if (record.id && languageById.has(record.id)) {
            record.language = languageById.get(record.id);
          }
        }
        this.captionTemplateLanguagesSupported = true;
        // The list may already have been built (unfiltered) before the
        // stitch landed — rebuild so language variants filter correctly.
        this.refreshKaraokeTemplatesForLanguage();
      } catch (err) {
        // Expected on environments where the PLAT-11411 migration hasn't run
        // (Dato GraphQL rejects the unknown `language` field).
        this.captionTemplateLanguagesSupported = false;
        this.logger.warn(
          'Caption template languages unavailable — per-language template variants disabled for this environment',
          extractErrorInfo(err),
        );
      }
    })();
    return this.karaokeTemplateLanguagesPromise;
  }

  /**
   * PLAT-11416 — whether this environment supports per-language caption
   * template variants, waiting for the probe when it hasn't resolved yet.
   * Used by the AR-variant flow so its language tagging can never diverge
   * from what `unpackKaraokeMetadata` will actually write.
   */
  async ensureCaptionTemplateLanguagesLoaded(): Promise<boolean> {
    await this.loadKaraokeTemplateLanguages();
    return this.captionTemplateLanguagesSupported === true;
  }

  /**
   * PLAT-11416 — rebuild the karaoke template list for the current caption
   * language (list build only — none of `getAllBrandingTemplates`'
   * select/auto-apply side effects). Called after a language switch and
   * after the language stitch resolves; no-op before the first build.
   */
  refreshKaraokeTemplatesForLanguage(): void {
    if (this.lastKaraokeTemplatesAspectRatio) {
      this.loadAllTemplates('karaoke', this.lastKaraokeTemplatesAspectRatio);
    }
  }

  /**
   * The DatoCMS record behind a karaoke template id, or null when the id belongs
   * to neither the showcase's own brandings nor a global preset.
   *
   * The ids used across the UI are *display* ids: a global preset is keyed by
   * `presetId || id` (see `loadKaraokePresets`), so the same preset answers to a
   * slug like `blackAndWhite` in one place and to its record id in another. Use
   * this whenever an id has to become a DatoCMS link or be stored as a config —
   * `brandingTemplates` holds display copies whose colors are already strings.
   */
  public getKaraokeBrandingRecord(
    id: string | undefined,
  ): KaraokeTextBranding | null {
    if (!id) return null;
    const showcaseRecord =
      this.videoCreator.organization?.karaokeTextBranding?.find(
        b => b.id === id,
      );
    if (showcaseRecord) return showcaseRecord;
    return (
      this.globalKaraokePresetRecords?.find(
        b => (b.presetId || b.id) === id || b.id === id,
      ) ?? null
    );
  }

  private loadAllTemplates(
    type: 'basic' | 'karaoke' = 'basic',
    aspect_ratio: GenericTextBranding['aspectRatio'] = this.videoCreator.getDefaultAspectRatio(),
  ) {
    let templates: Template[] = [];
    if (type === 'basic') {
      const textBrandings =
        this.videoCreator.organization?.lowerThirdTextBranding
          ?.filter(b => b.aspectRatio === aspect_ratio)
          ?.map(branding => {
            return this.getBrandingProps(branding, type);
          }) || [];
      const defaultProps = this.getDefaultBasicTextConfig();
      const customProps = {
        ...defaultProps,
        id: GenericPresetTemplates.custom,
        title: 'Custom',
      };
      templates = [defaultProps, customProps, ...textBrandings] as Template[];
    } else {
      // PLAT-11416 — kick the one-time language stitch; until it resolves,
      // records have no `language` and the filter below passes everything —
      // the stitch rebuilds this list when it lands.
      void this.loadKaraokeTemplateLanguages();
      this.lastKaraokeTemplatesAspectRatio = aspect_ratio;
      const allKaraokeBrandings =
        this.videoCreator.organization?.karaokeTextBranding || [];
      // A language-tagged variant only belongs in the picker while its
      // language is the active caption language; neutral records (no
      // language) show everywhere, as before PLAT-11416.
      const activeLanguageKey = this.getActiveCaptionLanguageKey();
      const textBrandings = allKaraokeBrandings
        .filter(
          b =>
            b.aspectRatio === aspect_ratio &&
            (!b.language?.trim() ||
              captionVariantMatchesLanguage(b.language, activeLanguageKey)),
        )
        .map(branding => {
          return this.getBrandingProps(branding, type);
        });
      const presets = this.globalKaraokePresets.filter(
        p => p.aspectRatio === aspect_ratio,
      );
      templates = [...textBrandings, ...presets];
    }
    this.brandingTemplates = templates;
    this.loadedTemplateKeys.add(`${type}:${aspect_ratio}`);
  }

  public getAllBrandingTemplates(
    type: 'basic' | 'karaoke' = 'basic',
    aspect_ratio: GenericTextBranding['aspectRatio'] = this.videoCreator.getDefaultAspectRatio(),
    hasActiveElement: boolean = false,
    onKaraokeInitialize = false,
    source: ElementState['source'] | null = null,
  ) {
    this.loadAllTemplates(type, aspect_ratio);
    this.loadLastUsedTemplate(type, aspect_ratio);
    const templates = this.brandingTemplates;

    let templateId: string | undefined =
      this.selectedTemplateId || this.lastUsedTemplateId;

    if (hasActiveElement) {
      templateId =
        this.getTemplateIdOnSourceChange(type) || GenericPresetTemplates.custom;
    } else if (templateId === GenericPresetTemplates.custom) {
      templateId = GenericPresetTemplates.default;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      console.log(
        `TextBrandingService | getAllBrandingTemplates | selectedBrandingTemplate:`,
        { type, template },
      );
      this.selectedBrandingTemplate = template;
      this.selectedTemplateId = templateId;
    } else {
      const _template =
        (hasActiveElement
          ? templates.find(t => t.id === GenericPresetTemplates.custom)
          : templates.find(t => t.id === GenericPresetTemplates.default)) ||
        templates[0];
      if (!_template) {
        // No templates available (e.g., opted-out customer with no
        // showcase-owned karaoke templates for this aspect ratio).
        this.selectedBrandingTemplate = undefined;
        this.selectedTemplateId = GenericPresetTemplates.default;
        return;
      }
      this.selectedBrandingTemplate = _template;
      this.selectedTemplateId = _template.id as string;
      console.warn(
        'Branding template not found could be due to change in aspect ratio - revert to default:',
        templateId,
      );
    }

    if (
      this.selectedTemplateId !== GenericPresetTemplates.custom &&
      !onKaraokeInitialize &&
      !hasActiveElement
    ) {
      this.applyTemplate(this.selectedBrandingTemplate!, type, source);
    }
    this.closeActiveState = true;
  }

  public getTemplateIdOnSourceChange(
    type: 'basic' | 'karaoke' = 'basic',
    configId: string | null = null,
  ) {
    if (type === 'basic') {
      const config = this.videoCreator.textProducer.getTextConfig();
      const id = configId || (config.id as string);
      if (id) {
        return (
          this.videoCreator.currentVideo?.extraElementData as ExtraElementData
        )?.lowerThirdTemplates?.[id];
      }
    } else {
      const config = configId
        ? this.findKaraokeConfigForClip(configId)
        : this.videoEditor.getDefaultKaraokeConfig();
      return config?.parent_template_id;
    }
  }

  private findKaraokeConfigForClip(clipId: string): KaraokeConfig | undefined {
    // PLAT-11905 — while the dub-timed captions are on screen, their clip ids
    // exist only in the shown rows: the saved shape holds the source-timed
    // rows. Every other case reads the saved shape, as before.
    const karaokeStore = this.videoCreator.rootStore.karaokeStore;
    if (karaokeStore.liveCaptionKind === 'dub') {
      const shownClip = karaokeStore.karaokeLines.find(c => c.id === clipId);
      return shownClip?.karaokeConfigId
        ? this.videoEditor.getKaraokeConfigs()?.get(shownClip.karaokeConfigId)
        : undefined;
    }

    const karaokeData =
      this.videoCreator.rootStore.karaokeStore.getKaraokeData();
    const configsMap = this.videoEditor.getKaraokeConfigs();

    if (karaokeData && configsMap) {
      const clip = karaokeData.clips.find(c => c.id === clipId);
      if (clip?.karaokeConfigId) {
        const config = configsMap?.get(clip.karaokeConfigId);
        if (config) return config;
      }
    }

    return undefined;
  }

  public switchTemplateByType(selectedElementsIds: string[]) {
    if (selectedElementsIds.length !== 1) return;
    const elementId = selectedElementsIds[0];
    const element = this.videoCreator.renderer
      ?.getElements()
      .find(e => e.source.id === elementId);
    if (!element) return;

    if (this.isTemplateModified) {
      this.isTemplateModified = false;
    }

    if (
      element.source.type === 'text' ||
      element.source.track === KARAOKE_TRACK_NUMBER
    ) {
      const type =
        element.source.track === KARAOKE_TRACK_NUMBER ? 'karaoke' : 'basic';
      if (type === 'basic') {
        this.closeActiveState = true;
      }

      if (type === 'basic') {
        this.videoCreator.textProducer.setTextConfig(element.source);
      }

      this.switchTemplateOnSourceChange(type, elementId, element);
    }
  }

  private switchTemplateOnSourceChange(
    type: 'basic' | 'karaoke' = 'basic',
    elementId: string | null = null,
    element: ElementState,
  ) {
    if (type === 'basic' && this.videoCreator.textProducer.creatingNewText) {
      this.videoCreator.textProducer.creatingNewText = false;
      return;
    }
    let templateId = this.getTemplateIdOnSourceChange(
      type,
      elementId,
    ) as string;

    if (templateId === this.selectedTemplateId) return;

    if (
      (templateId === GenericPresetTemplates.custom ||
        templateId === GenericPresetTemplates.default) &&
      elementId
    ) {
      this.selectedTemplateId = templateId;
      this.selectedBrandingTemplate = this.brandingTemplates.find(
        t => t.id === templateId,
      );
      if (element.source.track !== KARAOKE_TRACK_NUMBER) {
        this.videoCreator.textProducer.setTextConfig(element.source!);
      }
      return;
    }

    const template = this.brandingTemplates.find(t => t.id === templateId);

    if (template) {
      this.selectedTemplateId = templateId;
      this.selectedBrandingTemplate = template;
    } else {
      this.selectedTemplateId = GenericPresetTemplates.custom;
      this.selectedBrandingTemplate = this.brandingTemplates[1];
    }
  }

  public selectBrandingTemplate(type: 'basic' | 'karaoke', templateId: string) {
    const template = this.brandingTemplates.find(t => t.id === templateId);
    if (template) {
      this.selectedTemplateId = templateId;
      this.selectedBrandingTemplate = template;
      this.saveToExtraElementData(type, templateId);
    }
  }

  /**
   * Convert a DatoColor object or string to an rgba() string.
   * Handles: DatoColor objects, rgba/rgb strings, named colors, null/undefined.
   */
  private datoColorToRgbaString(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value;
    // DatoColor object — alpha is 0-255 in DatoCMS
    if (
      typeof value === 'object' &&
      'red' in value &&
      'green' in value &&
      'blue' in value
    ) {
      return datoColorToRgba(value as DatoColor);
    }
    return null;
  }

  public splitRgbaString(rgba: string, skipError = false) {
    if (!rgba) return [];
    if (typeof rgba !== 'string') {
      if (skipError) return [];
      throw new Error('Bad rgba value: expected string');
    }
    if (!rgba.includes('rgb')) {
      if (skipError) return [];
      throw new Error('Bad rgba value');
    }

    // Handle CSS Color Level 4 space-separated format: rgb(R G B / A)
    const spaceMatch = rgba.match(
      /rgba?\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\s*\)/,
    );
    if (spaceMatch) {
      const [, r, g, b, a] = spaceMatch;
      return [r, g, b, a ?? '1'];
    }

    if (rgba.includes('rgba'))
      return rgba
        .substring(5, rgba.length - 1)
        .split(',')
        .map(value => value.trim());
    const value = rgba
      .substring(4, rgba.length - 1)
      .split(',')
      .map(value => value.trim());
    return [...value, '1'];
  }

  public rgbaToHex(rgba: string, skipError = false) {
    const rgbaValues = this.splitRgbaString(rgba, skipError);
    if (rgbaValues.length === 0) return '';
    const hexValues = rgbaValues.slice(0, 3).map(value => {
      const hex = Number(value).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    });
    const alphaHex = Math.round(parseFloat(rgbaValues[3]) * 255).toString(16);
    const hexAlpha = (alphaHex.length === 1 ? '0' : '') + alphaHex;
    if (hexAlpha.toLowerCase() === 'ff') return '#' + hexValues.join('');
    return '#' + hexValues.join('') + hexAlpha;
  }

  public getRgbaValueFromRgbaString(color: string) {
    if (!color) return null;
    const [red, green, blue, alpha] = this.splitRgbaString(color, true);
    return {
      red: parseInt(red),
      green: parseInt(green),
      blue: parseInt(blue),
      alpha: parseFloat(alpha) * 255,
    };
  }

  public addAlphaToRgba(rgba: string, alpha: string | undefined) {
    if (!alpha) return rgba;
    const rgbaValues = rgba
      .substring(5, rgba.length - 1)
      .split(',')
      .map(value => value.trim());
    return `rgba(${rgbaValues[0]}, ${rgbaValues[1]}, ${rgbaValues[2]}, ${alpha})`;
  }

  public rgbaWithSolidAlpha(rgba: string) {
    if (!rgba) return '';
    const rgbaValues = this.splitRgbaString(rgba, true);
    return `rgba(${rgbaValues[0]}, ${rgbaValues[1]}, ${rgbaValues[2]}, 1)`;
  }

  public snakeToCamel(str: string) {
    return str.replace(/_([a-z])/g, function (match, letter) {
      return letter.toUpperCase();
    });
  }

  public camelToSnake(str: string): string {
    return str.replace(/[A-Z]/g, (letter, index) => {
      return index === 0 ? letter.toLowerCase() : `_${letter.toLowerCase()}`;
    });
  }

  getColorInColorCode(color: string) {
    if (color === 'white') return 'rgba(255, 255, 255, 1)';
    if (color === 'black') return 'rgba(0, 0, 0, 1)';
    if (color === 'transparent') return null;
    return color;
  }

  private getGenericBrandingValues(
    config: KaraokeConfig | Record<string, any>,
    title: string,
    type: 'basic' | 'karaoke' = 'basic',
  ) {
    const values: Partial<
      Record<BrandingKeys, string | boolean | number | null | DatoColor>
    > = {
      aspect_ratio:
        this.videoCreator.currentVideo?.aspectRatio ||
        this.videoCreator.getDefaultAspectRatio(),
      title,
    };

    const directFields = [
      'font_family',
      'font_weight',
      'x_alignment',
      'background_align_threshold',
      'background_border_radius',
    ];
    const COLOR_BRANDING = ['fillColor', 'backgroundColor', 'shadowColor'];

    if (type === 'karaoke') {
      directFields.push('stroke_width');
      COLOR_BRANDING.push('strokeColor', 'transcriptColor');
    }

    for (let [key, value] of Object.entries(config)) {
      if (key === 'font_size') {
        values[key as BrandingKeys] = this.videoCreator.textProducer
          .getFontSize(value as string)
          .toString();
      }
      if (directFields.includes(key)) {
        values[key as BrandingKeys] = value.toString();
      }

      if (key === 'text_transform') {
        values.uppercase = value === 'uppercase';
      }

      if (key === 'font_style') {
        values.italic = value === 'italic';
      }

      if (COLOR_BRANDING.includes(this.snakeToCamel(key))) {
        const colorCode = value ? this.getColorInColorCode(value) : null;
        if (!colorCode) {
          values[key as BrandingKeys] = null;
          continue;
        }
        const color = this.getRgbaValueFromRgbaString(colorCode as string);
        if (!color || isNaN(color.red)) {
          values[key as BrandingKeys] = null;
          continue;
        }
        values[key as BrandingKeys] = color as DatoColor;
        if (key === 'background_color' && color) {
          values.background_transparency = (color.alpha / 255) * 100;
        }
      }
    }

    values.shadow_blur = config.shadow_blur;
    values.shadow_x_offset = config.shadow_x;
    values.shadow_y_offset = config.shadow_y;

    return values;
  }

  private formatLowerThirdBrandingUpdateFields(
    config: Record<string, any>,
    title: string,
  ) {
    const values = this.getGenericBrandingValues(config, title, 'basic');

    const enterAnimation = config?.animations?.find(
      (a: any) => a.time === 'start' || a.time === 0,
    );
    if (enterAnimation) {
      values.enter_animation = enterAnimation.type;
      values.enter_animation_length = enterAnimation.duration;
    }

    const exitAnimation = config?.animations?.find(
      (a: any) => a.time === 'end',
    );
    if (exitAnimation) {
      values.exit_animation = exitAnimation.type;
      values.exit_animation_length = exitAnimation.duration;
    }

    if (config.x) {
      values.x_pos = Math.max(0, Math.min(100, parseFloat(config.x as string)));
    }

    if (config.y) {
      values.y_pos = Math.max(0, Math.min(100, parseFloat(config.y as string)));
    }

    return values;
  }

  private formatKaraokeBrandingUpdateFields(
    config: KaraokeConfig | Record<string, any>,
    title: string,
  ) {
    const values: Partial<
      Record<BrandingKeys, string | boolean | number | null | DatoColor>
    > = {};

    values.transcript_effect = config.transcript_effect || ''; // empty string means no effect for datocms
    // transcript_maximum_length (words per line) should always be persisted,
    // even when transcript_effect is not set — it's an independent setting.
    values.transcript_maximum_length = config.transcript_maximum_length ?? null;
    // Same for the line-break style. Empty string means none for datocms.
    values.line_break_style = config.line_break_style || '';
    // transcript_effect tells if user activated the transcript settings
    if (values.transcript_effect) {
      values.transcript_split =
        config.transcript_split ||
        DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_split;
      values.transcript_placement =
        config.transcript_placement ||
        DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_placement;
      // important to prepare this value upfront, so that
      // getGenericBrandingValues can convert it to DatoColor
      values.transcript_color =
        config.transcript_color ||
        DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color;
    }

    Object.assign(
      values,
      this.getGenericBrandingValues({ ...config, ...values }, title, 'karaoke'),
    );

    values.hide_comma = !!config.hideComma;
    values.hide_fillers = !!config.hideFillers;
    values.hide_period = !!config.hidePeriod;

    if (config.x) {
      values.x_pos = Math.max(0, Math.min(100, parseFloat(config.x as string)));
    }

    if (config.y) {
      values.y_pos = Math.max(0, Math.min(100, parseFloat(config.y as string)));
    }

    if (config.instagramEffect) {
      values.animation = 'instagram';
    } else {
      const animation = config.animations?.find(
        (a: any) => a.time === 'start' || a.time === 0,
      );
      if (animation) values.animation = animation.type;
    }

    if (config.instagramLines)
      values.instagram_lines = config.instagramLines.toString();

    if (this.videoCreator.organization?.id) {
      values.referencing_showcase = this.videoCreator.organization.id;
    }

    return values;
  }

  private formatBrandingUpdateFields(
    config: KaraokeConfig | Record<string, any>,
    title: string,
    type: 'basic' | 'karaoke' = 'basic',
  ) {
    if (type === 'basic') {
      return this.formatLowerThirdBrandingUpdateFields(config, title);
    }
    return this.formatKaraokeBrandingUpdateFields(config, title);
  }

  public formatOrganizationBrandingFields(
    config: KaraokeConfig | Record<string, any>,
    title: string,
    type: 'basic' | 'karaoke' = 'basic',
    id?: string,
    presetId?: string,
  ) {
    type TextBranding = KaraokeTextBranding & LowerThirdTextBranding;

    const fields = this.formatBrandingUpdateFields(config, title, type);

    // Start from existing branding so fields not in the DatoCMS update payload
    // (e.g. transcriptMaximumLength when transcript_effect is falsy) are preserved.
    const existingBranding: Array<{ id?: string }> =
      type === 'basic'
        ? this.videoCreator.organization?.lowerThirdTextBranding || []
        : this.videoCreator.organization?.karaokeTextBranding || [];
    const existingEntry = existingBranding.find(b => b.id === id);
    let values = { ...(existingEntry || {}), id } as TextBranding;

    for (let [k, value] of Object.entries(fields)) {
      const key = this.snakeToCamel(k) as keyof TextBranding;
      values = { ...values, [key]: value ?? undefined };
      if (COLOR_BRANDING.includes(key)) {
        const colorValue = this.getColorInColorCode(
          config[k as keyof KaraokeConfig] as string,
        );

        if (!colorValue) {
          values = { ...values, [key as keyof TextBranding]: null };
          continue;
        }

        const [red, green, blue, alpha] = this.splitRgbaString(
          colorValue as string,
          true,
        );

        if (!red || isNaN(parseInt(red))) {
          values = { ...values, [key as keyof TextBranding]: null };
          continue;
        }

        const datoColor: DatoColor = {
          red: parseInt(red),
          green: parseInt(green),
          blue: parseInt(blue),
          alpha: parseFloat(alpha) * 255,
          hex: this.rgbaToHex(colorValue as string, true),
          cssRgb: `rgb(${red} ${green} ${blue} / ${alpha})`,
        };
        values = { ...values, [key as keyof TextBranding]: datoColor };
      }
    }

    // `preset_id` travels to DatoCMS via `unpackKaraokeMetadata`, not through
    // the branding payload above, so it has to be set here too — otherwise the
    // in-memory entry for a freshly saved template reads as family-less and the
    // next aspect-ratio switch re-roots the preset family on it (PLAT-11542).
    if (presetId) {
      values = { ...values, presetId } as TextBranding;
    }

    const branding =
      type === 'basic'
        ? this.videoCreator.organization?.lowerThirdTextBranding || []
        : this.videoCreator.organization?.karaokeTextBranding || [];

    const brandingIdx = branding.findIndex(b => b.id === id);

    let updatedOrganization = this.videoCreator.organization!;

    if (brandingIdx > -1) {
      branding[brandingIdx] = values;
      if (type === 'karaoke') {
        updatedOrganization.karaokeTextBranding =
          branding as KaraokeTextBranding[];
      } else {
        updatedOrganization.lowerThirdTextBranding =
          branding as LowerThirdTextBranding[];
      }
    } else {
      updatedOrganization = {
        ...this.videoCreator.organization!,
        ...(type === 'basic'
          ? {
              lowerThirdTextBranding: [
                ...(this.videoCreator.organization?.lowerThirdTextBranding ||
                  []),
                values as LowerThirdTextBranding,
              ],
            }
          : {
              karaokeTextBranding: [
                ...(this.videoCreator.organization?.karaokeTextBranding || []),
                values as KaraokeTextBranding,
              ],
            }),
      };
    }

    this.videoCreator.organization = updatedOrganization;
    return updatedOrganization;
  }

  /**
   * Syncs a template to the organization's branding array and persists it to DatoCMS.
   * This ensures the organization's in-memory branding array stays in sync with DatoCMS.
   *
   * @param config - The karaoke or basic text configuration
   * @param title - The template title
   * @param type - Template type ('basic' or 'karaoke')
   * @param templateId - The DatoCMS template ID
   * @param aspectRatio - Aspect ratio for lastUsedTemplates (optional, defaults to current aspect ratio)
   */
  private async syncTemplateToOrganization(
    config: KaraokeConfig | Record<string, any>,
    title: string,
    type: 'basic' | 'karaoke',
    templateId: string,
    aspectRatio?: AspectRatio,
    presetId?: string,
  ): Promise<void> {
    const updatedOrganization = this.formatOrganizationBrandingFields(
      config,
      title,
      type,
      templateId,
      presetId,
    );

    const lastUsedTemplates = this.setLastUsedTemplate(
      templateId,
      title,
      type,
      aspectRatio,
    );
    updatedOrganization.lastUsedTemplates = lastUsedTemplates;

    await this.datoClientStore.albumRepository?.update(updatedOrganization);
  }

  /**
   * Unpacks optional metadata fields for DatoCMS payload
   */
  private unpackKaraokeMetadata(metadata: KaraokeTemplateMetadata) {
    return {
      ...(metadata.aspectRatio ? { aspect_ratio: metadata.aspectRatio } : {}),
      ...(metadata.presetId ? { preset_id: metadata.presetId } : {}),
      // Only when the environment's schema has the field (PLAT-11411) —
      // Dato rejects writes naming unknown fields.
      ...(metadata.language && this.captionTemplateLanguagesSupported === true
        ? { language: metadata.language }
        : {}),
    };
  }

  /**
   * Core template creation logic - creates a new karaoke template in DatoCMS
   * Used by both save() and KaraokeTemplateVariantService
   */
  public async createKaraokeTemplateInDato(
    config: KaraokeConfig,
    metadata: KaraokeTemplateMetadata,
  ): Promise<string> {
    const brandingUpdateParams = this.formatKaraokeBrandingUpdateFields(
      config,
      metadata.title,
    );

    const itemType = await this.datoClientStore.datoClient?.itemTypes.find(
      'karaoke_text_branding',
    );

    const savedBranding = await this.datoClientStore.datoClient?.items.create({
      item_type: { type: 'item_type', id: itemType!.id },
      ...brandingUpdateParams,
      ...this.unpackKaraokeMetadata(metadata),
    });

    return savedBranding!.id;
  }

  /**
   * Core template update logic - updates an existing karaoke template in DatoCMS
   */
  public async updateKaraokeTemplateInDato(
    templateId: string,
    config: KaraokeConfig,
    metadata: KaraokeTemplateMetadata,
  ): Promise<void> {
    const brandingUpdateParams = this.formatKaraokeBrandingUpdateFields(
      config,
      metadata.title,
    );

    await this.datoClientStore.datoClient?.items.update(templateId, {
      ...brandingUpdateParams,
      ...this.unpackKaraokeMetadata(metadata),
    });
  }

  /**
   * Private helper to create a new basic text template in DatoCMS
   */
  private async createBasicTextTemplateInDato(
    config: Record<string, any>,
    title: string,
  ): Promise<string> {
    const brandingUpdateParams = this.formatLowerThirdBrandingUpdateFields(
      config,
      title,
    );

    const itemType = await this.datoClientStore.datoClient?.itemTypes.find(
      'lower_third_text_branding',
    );

    const savedBranding = await this.datoClientStore.datoClient?.items.create({
      item_type: { type: 'item_type', id: itemType!.id },
      ...brandingUpdateParams,
    });

    return savedBranding!.id;
  }

  /**
   * Private helper to update an existing basic text template in DatoCMS
   */
  private async updateBasicTextTemplateInDato(
    templateId: string,
    config: Record<string, any>,
    title: string,
  ): Promise<void> {
    const brandingUpdateParams = this.formatLowerThirdBrandingUpdateFields(
      config,
      title,
    );

    await this.datoClientStore.datoClient?.items.update(
      templateId,
      brandingUpdateParams,
    );
  }

  /**
   * Creates or updates a template from the current styling. Which one it
   * does is inferred from the selection unless `forceAction` says.
   *
   * Resolves `ok: true` when the template was persisted and `ok: false`
   * when it was not — no organization to save into, or the request failed.
   * It never throws: the failure is logged here and reported to the caller
   * through the return value so the UI can tell the user (PLAT-11653)
   * instead of leaving the click unacknowledged.
   *
   * A failure carries `created` when a record had already been written
   * before the throw; passing it back as `resume` retries into that record
   * rather than creating another.
   */
  public async save(
    newTitle: string,
    type: 'basic' | 'karaoke' = 'basic',
    setting: 'text-settings' | 'karaoke-settings' | null = null,
    prevConfig: ElementState['source'] | null = null,
    forceAction: 'update' | 'create' | undefined = undefined,
    resume?: CreatedTemplateRecord,
  ): Promise<TemplateSaveResult> {
    const album = this.videoCreator.organization;
    if (!album) return { ok: false };

    // Declared outside the try so the catch can report what this attempt
    // already committed.
    let createdRecord: CreatedTemplateRecord | undefined = resume;

    try {
      this.isLoading = true;
      const karaokeConfig = this.getCurrentKaraokeConfig();
      const lowerThirdConfig = this.videoCreator.textProducer.getTextConfig();

      let templateId = (this.modifiedTemplateData?.template?.id ||
        this.selectedTemplateId) as string;

      let config =
        type === 'basic' || setting === 'text-settings'
          ? lowerThirdConfig
          : karaokeConfig;

      if (prevConfig) config = prevConfig;

      const title =
        newTitle || (this.selectedBrandingTemplate!.title as string);

      const aspectRatio =
        this.videoCreator.currentVideo?.aspectRatio ||
        this.videoCreator.getDefaultAspectRatio();

      const isExistingTemplate =
        this.selectedBrandingTemplate &&
        templateId !== GenericPresetTemplates.default &&
        templateId !== GenericPresetTemplates.custom;

      const autoIsUpdate =
        prevConfig || (this.isTemplateModified && isExistingTemplate);
      const action =
        forceAction === 'update' || forceAction === 'create'
          ? forceAction
          : autoIsUpdate
            ? 'update'
            : 'create';
      const isUpdate = action === 'update';

      // PLAT-11416 — a captions-surface save while the captions are in a
      // non-source language tags (create) or targets (update) that
      // language's variant instead of the base record; null = plain save,
      // exactly as before. Graphics → Text ('text-settings') saves a
      // text-derived template, where the caption language is irrelevant.
      const variantLanguageKey =
        type === 'karaoke' && setting !== 'text-settings'
          ? this.getCaptionVariantKeyForSave(isUpdate ? templateId : null)
          : null;
      let variantPresetId: string | null = null;

      if (isUpdate) {
        if (type === 'karaoke') {
          if (variantLanguageKey) {
            // "Update" of a base template from another language: create the
            // family's variant for that language rather than overwriting the
            // base record's styling.
            if (resume) {
              // Creating again would leave two records sharing a family key,
              // language and aspect ratio — which every family resolver picks
              // between by array order, so the user's edits would land on one
              // twin and the editor would render the other.
              templateId = resume.templateId;
              variantPresetId = resume.presetId ?? null;
              await this.updateKaraokeTemplateInDato(
                templateId,
                config as KaraokeConfig,
                {
                  title,
                  aspectRatio,
                  presetId: resume.presetId,
                  language: variantLanguageKey,
                },
              );
            } else {
              const created = await this.createKaraokeLanguageVariantInDato(
                templateId,
                config as KaraokeConfig,
                title,
                aspectRatio,
                variantLanguageKey,
              );
              templateId = created.templateId;
              variantPresetId = created.presetId;
              createdRecord = { templateId, presetId: created.presetId };
            }
            this.selectedTemplateId = templateId;
          } else {
            await this.updateKaraokeTemplateInDato(
              templateId,
              config as KaraokeConfig,
              { title, aspectRatio },
            );
          }
        } else {
          await this.updateBasicTextTemplateInDato(templateId, config, title);
        }
      } else {
        if (type === 'karaoke') {
          if (resume) {
            templateId = resume.templateId;
          } else {
            templateId = await this.createKaraokeTemplateInDato(
              config as KaraokeConfig,
              { title, aspectRatio, language: variantLanguageKey ?? undefined },
            );
            createdRecord = { templateId, presetId: templateId };
          }
          // Set presetId to the template's own ID so variant service can find it later
          // (also stamps a resumed record, whose own stamp may be what failed).
          await this.updateKaraokeTemplateInDato(
            templateId,
            config as KaraokeConfig,
            { title, aspectRatio, presetId: templateId },
          );
          variantPresetId = templateId;
        } else if (resume) {
          templateId = resume.templateId;
          await this.updateBasicTextTemplateInDato(templateId, config, title);
        } else {
          templateId = await this.createBasicTextTemplateInDato(config, title);
          createdRecord = { templateId };
        }
        this.selectedTemplateId = templateId;
      }

      await this.syncTemplateToOrganization(
        config,
        title,
        type,
        templateId,
        aspectRatio,
        // A create just stamped `presetId` = own id; an update keeps whatever
        // the existing entry already carries.
        isUpdate ? undefined : templateId,
      );

      // PLAT-11416 — formatOrganizationBrandingFields builds the in-memory
      // record from the config alone; stitch the variant metadata so pickers
      // and the language-switch resolver see it without a reload.
      if (type === 'karaoke' && variantLanguageKey) {
        const record =
          this.videoCreator.organization?.karaokeTextBranding?.find(
            b => b.id === templateId,
          );
        if (record) {
          record.language = variantLanguageKey;
          if (!record.presetId && variantPresetId) {
            record.presetId = variantPresetId;
          }
        }
      }

      if (type === 'karaoke') {
        // A freshly created record — a new template or a new language
        // variant — becomes the config's parent.
        if (!isUpdate || variantLanguageKey) {
          this.syncKaraokeConfigWithNewTemplateId(templateId);
        }
        if (!this.videoCreator.currentVideo?.id) {
          await this.videoCreator.saveStoryAndVideo();
        } else {
          const preparedVideo = await backgroundVideoSaveService.prepare(
            this.videoCreator,
          );
          // Fire-and-forget actual save
          backgroundVideoSaveService.save(preparedVideo, this.videoCreator);
        }
        // prevents false positive when detecting if there's a new version available
        if (this.videoCreator.currentVideo) {
          try {
            await this.videoCreator.loadCurrentVideoVersionsHistory(0);
          } catch (err) {
            this.logger.warn(
              'Non-fatal: Failed to refresh version history after template save',
              extractErrorInfo(err),
            );
          }
        }
      }

      if (isUpdate) {
        // For updates, only reload the template list and update the selected
        // template reference. Do NOT re-apply the template — the editor already
        // has the correct state and re-applying would overwrite it with
        // in-memory template values that may be stale.
        this.loadAllTemplates(type, aspectRatio);
        const updatedTemplate = this.brandingTemplates.find(
          t => t.id === templateId,
        );
        if (updatedTemplate) {
          this.selectedBrandingTemplate = updatedTemplate;
          this.selectedTemplateId = templateId;
        }
      } else if (!prevConfig) {
        const element = this.videoCreator.getActiveElement()?.source || null;
        this.getAllBrandingTemplates(type, aspectRatio, false, false, element);
      } else {
        this.loadAllTemplates(type, aspectRatio);
      }

      this.toggleModifiedTemplateData();
      this.prepareTemplateToSave = false;
      return { ok: true };
    } catch (error) {
      this.logger.error('Failed to save template', {
        ...extractErrorInfo(error),
        type,
        setting,
        action: forceAction,
        resumedFrom: resume?.templateId,
      });
      return { ok: false, created: createdRecord };
    } finally {
      this.isLoading = false;
    }
  }

  private syncKaraokeConfigWithNewTemplateId(templateId: string): void {
    this.videoEditor.commandUpdateKaraokeConfig({
      ...this.getCurrentKaraokeConfig(),
      parent_template_id: templateId,
    });
    this.videoCreator.rootStore.karaokeStore.syncFromEditor(this.videoEditor);
  }

  private genericConfigToApply(
    template: Record<
      keyof (KaraokeTextBranding | LowerThirdTextBranding),
      string | number | boolean | null
    >,
    defaultSetting: Record<string, any>,
  ) {
    return {
      font_family: (template.fontFamily as string) || defaultSetting.fontFamily,
      font_size: (template.fontSize || defaultSetting.fontSize) as string,
      fill_color: this.datoColorToRgbaString(template.fillColor) ?? 'white',
      background_color: this.datoColorToRgbaString(template.backgroundColor),
      background_align_threshold: (template.backgroundAlignThreshold ||
        defaultSetting.backgroundAlignThreshold) as string,
      background_border_radius: (template.backgroundBorderRadius ||
        defaultSetting.backgroundBorderRadius) as string,
      font_weight: (template.fontWeight || defaultSetting.fontWeight) as string,
      x_alignment: (template.xAlignment || '0%') as string,
      ...(template.uppercase
        ? { text_transform: 'uppercase' }
        : { text_transform: 'none' }),
      ...(template.italic
        ? { font_style: 'italic' }
        : { font_style: 'normal' }),
      ...(template.shadowColor &&
        template.shadowBlur != null &&
        template.shadowXOffset != null &&
        template.shadowYOffset != null && {
          shadow_color: this.datoColorToRgbaString(template.shadowColor),
          shadow_blur: template.shadowBlur as number,
          shadow_x: template.shadowXOffset as number,
          shadow_y: template.shadowYOffset as number,
        }),
    };
  }

  public saveToExtraElementData(type: 'karaoke' | 'basic', templateId: string) {
    if (!this.videoCreator.currentVideo) {
      throw new Error(
        'Cannot save to extra element data: currentVideo not set',
      );
    }
    if (type === 'basic') {
      if (this.videoCreator.textProducer.config?.id) {
        const existingData =
          this.videoCreator.currentVideo?.extraElementData || {};

        this.videoCreator.currentVideo.extraElementData = {
          ...existingData,
          lowerThirdTemplates: {
            ...(existingData?.lowerThirdTemplates || {}),
            [this.videoCreator.textProducer.config.id]: templateId,
          } as ExtraElementData | KaraokeConfig | null | undefined,
        };
      }
    } else {
      this.videoCreator.currentVideo.extraElementData = {
        ...(this.videoCreator.currentVideo?.extraElementData || {}),
        karaokeTemplateId: templateId as
          | ExtraElementData
          | KaraokeConfig
          | null
          | undefined,
      };
    }
  }

  public formatPositionValue(value: string | number | boolean | null): string {
    if (value === null || value === undefined) return '0%';
    if (typeof value === 'boolean') return '0%';
    const stringValue = value.toString();
    if (stringValue.endsWith('%')) return stringValue;
    return `${stringValue}%`;
  }

  public async applyLowerThirdTemplate(
    template: LowerThirdTemplate,
    source: ElementState['source'] | null = null,
    useSource: boolean = false,
    newConfig: ElementState['source'] | null = null,
    applyPositioning: boolean = true,
  ) {
    const defaultSetting = this.getDefaultBasicTextConfig();

    let config: ElementState['source'] = this.genericConfigToApply(
      template,
      defaultSetting,
    );

    if (!template.shadowColor) {
      config.shadow_color = null;
      config.shadow_blur = undefined;
      config.shadow_x = undefined;
      config.shadow_y = undefined;
    }

    const enterAnimation = template.enterAnimation;
    const exitAnimation = template.exitAnimation;
    const enterAnimationLength = template.enterAnimationLength;
    const exitAnimationLength = template.exitAnimationLength;

    if (enterAnimation && enterAnimation !== 'none') {
      config.animations = [
        ...(config.animations || []),
        {
          duration: enterAnimationLength || 1,
          time: 0,
          type: enterAnimation,
        },
      ];
    }

    if (exitAnimation && exitAnimation !== 'none') {
      config.animations = [
        ...(config.animations || []),
        {
          duration: exitAnimationLength || 1,
          time: 'end',
          reversed: true,
          type: exitAnimation,
        },
      ];
    }

    // Apply positioning if enabled and template has positioning data
    if (applyPositioning) {
      if (template.xPos !== undefined && template.xPos !== null) {
        config.x = this.formatPositionValue(template.xPos);
      }
      if (template.yPos !== undefined && template.yPos !== null) {
        config.y = this.formatPositionValue(template.yPos);
      }
    }

    if (source) {
      config = { ...config, text: source.text };
    }

    const elementId = useSource && source ? source.id : undefined;
    console.log('config', config);

    await this.videoCreator.textProducer.modifyProperty(
      { ...config, ...newConfig },
      true,
      elementId,
    );

    this.saveToExtraElementData('basic', template.id as string);

    return config;
  }

  public hasTemplatePositioning(template: LowerThirdTemplate): boolean {
    return (
      (template.xPos !== undefined && template.xPos !== null) ||
      (template.yPos !== undefined && template.yPos !== null)
    );
  }

  public doesElementMatchTemplatePosition(
    element: ElementState['source'],
    template: LowerThirdTemplate,
  ): boolean {
    if (!element || !template) return false;

    let xMatches = true;
    let yMatches = true;

    // Check x position
    if (template.xPos !== undefined && template.xPos !== null) {
      const templateX = this.formatPositionValue(template.xPos);
      xMatches = element.x === templateX;
    }

    // Check y position
    if (template.yPos !== undefined && template.yPos !== null) {
      const templateY = this.formatPositionValue(template.yPos);
      yMatches = element.y === templateY;
    }

    // Both must match (or not be defined in template)
    return xMatches && yMatches;
  }

  public transformKaraokeTemplateIntoConfig(
    template: KaraokeTemplate,
  ): KaraokeConfig {
    const config: Partial<KaraokeConfig> = this.genericConfigToApply(
      template,
      this.getDefaultKaraokeTextConfig(),
    );

    const karaokeConfig = DEFAULT_KARAOKE_CONFIG;
    const karaokeAnimation = template.animation;

    if (!karaokeAnimation || karaokeAnimation === 'none') {
      config.animations = [];
    }

    if (
      karaokeAnimation &&
      karaokeAnimation !== 'none' &&
      karaokeAnimation !== 'instagram'
    ) {
      config.animations = [
        {
          background_effect: 'animated',
          duration: karaokeConfig?.animations?.[0]?.duration || 1,
          time: 0,
          type: template.animation,
        },
      ];
    }

    if (karaokeAnimation === 'instagram') {
      config.instagramEffect = true;
      config.language = 'original';
      config.instagramLines = (template.instagramLines ||
        karaokeConfig.instagramLines) as 1 | 2 | 3 | 4 | 5 | 6 | 7;
      config.animations = [
        {
          background_effect: 'animated',
          duration: karaokeConfig?.animations?.[0]?.duration || 1,
          time: 0,
          type: 'text-appear',
        },
      ];
    } else {
      config.instagramEffect = false;
    }

    config.stroke_color =
      this.datoColorToRgbaString(template.strokeColor) ?? 'rgba(0, 0, 0, 1)';
    config.stroke_width = (template.strokeWidth ||
      karaokeConfig.stroke_width) as string;
    config.letter_spacing = template.letterSpacing as string;

    // Use parsePercent so an explicit 0 round-trips as "0%". A truthy check
    // here would collapse 0 to karaokeConfig.x/y and silently undo a user's
    // placement
    const parsedX = parsePercent(template.xPos);
    const parsedY = parsePercent(template.yPos);
    config.x = parsedX != null ? `${parsedX}%` : karaokeConfig.x;
    config.y = parsedY != null ? `${parsedY}%` : karaokeConfig.y;

    config.hideComma = !!template.hideComma;
    config.hidePeriod = !!template.hidePeriod;
    config.hideFillers = !!template.hideFillers;

    if (!template.shadowColor) {
      config.shadow_color = null;
      config.shadow_blur = undefined;
      config.shadow_x = undefined;
      config.shadow_y = undefined;
    }

    config.transcript_effect =
      (template.transcriptEffect as KaraokeConfig['transcript_effect']) ||
      undefined; // undefined means no effect for creatomate
    config.transcript_split =
      (template.transcriptSplit as KaraokeConfig['transcript_split']) ||
      undefined;
    config.transcript_placement =
      (template.transcriptPlacement as KaraokeConfig['transcript_placement']) ||
      undefined;
    config.transcript_maximum_length =
      (template.transcriptMaximumLength as KaraokeConfig['transcript_maximum_length']) ??
      null;
    config.line_break_style = isLineBreakStyle(template.lineBreakStyle)
      ? template.lineBreakStyle
      : null;
    config.transcript_color =
      this.datoColorToRgbaString(template.transcriptColor) || undefined;

    return {
      ...karaokeConfig,
      ...config,
    };
  }

  /**
   * PLAT-11877 — THE caption template a first "Captions on" applies, on every
   * surface (the clip preview's picker, Creator Studio's captions sidebar,
   * the language switch's seed, the review's preview): the one the showcase
   * used MOST RECENTLY at this aspect ratio (`organization.lastUsedTemplates`,
   * written by `applyTemplate`) when it is one of the showcase's own custom
   * templates; else the first custom template in the showcase's order; else
   * `undefined`, and the caller keeps the built-in "Impact" default. Never a
   * global preset and never a language variant. One rule, so the surfaces
   * cannot answer "which style will I get" differently — and so a menu can
   * say which one before the click (the head of
   * `getCustomKaraokeTemplateConfigs`).
   */
  public getDefaultCaptionTemplateConfig(
    aspectRatio: GenericTextBranding['aspectRatio'],
  ): { config: KaraokeConfig; templateId: string } | undefined {
    return this.getCustomKaraokeTemplateConfigs(aspectRatio)[0];
  }

  /**
   * PLAT-11702 follow-up — every caption template the showcase has at this
   * aspect ratio, in the order the Translation Review's Captions dropdown
   * lists them: the most recently used first (so the head of this list is the
   * default a first "on" applies, `getDefaultCaptionTemplateConfig`), then the
   * rest in the showcase's own order. Same set as the preferred resolver —
   * the showcase's own templates only, never a preset or a language variant —
   * carrying each one's name and face so the picker can draw the name in it.
   */
  public getCustomKaraokeTemplateConfigs(
    aspectRatio: GenericTextBranding['aspectRatio'],
  ): CustomKaraokeTemplateOption[] {
    const custom = this.customKaraokeBrandings(aspectRatio);
    if (custom.length === 0) return [];
    const lastUsedId = (this.videoCreator.organization?.lastUsedTemplates || [])
      .filter(t => t.type === 'karaoke' && t.aspectRatio === aspectRatio)
      .map(t => custom.find(b => b.id === t.id))
      .find((b): b is KaraokeTextBranding => !!b)?.id;
    const ordered = lastUsedId
      ? [
          ...custom.filter(b => b.id === lastUsedId),
          ...custom.filter(b => b.id !== lastUsedId),
        ]
      : custom;
    return ordered.map(branding => ({
      ...this.customKaraokeTemplateConfig(branding),
      title: branding.title,
      fontFamily: branding.fontFamily,
      fontWeight: branding.fontWeight,
      italic: !!branding.italic,
    }));
  }

  /**
   * PLAT-11702 follow-up — the showcase's name for a caption template id,
   * wherever the id came from: one of its own brandings (any aspect ratio,
   * language variants included), an Arbor preset, or a built-in preset. Used
   * by the Translation Review to name the row standing for the video's own
   * caption style, instead of a generic "Clip's Style". Undefined when
   * nothing carries that id — a template since deleted.
   */
  public getKaraokeTemplateTitle(
    templateId?: string | null,
  ): string | undefined {
    if (!templateId) return undefined;
    const branding = (
      this.videoCreator.organization?.karaokeTextBranding || []
    ).find(b => b.id === templateId || b.presetId === templateId);
    if (branding?.title) return branding.title;
    const preset = this.globalKaraokePresets.find(p => p.id === templateId);
    if (typeof preset?.title === 'string' && preset.title) return preset.title;
    return BUILT_IN_KARAOKE_TEMPLATE_TITLES[templateId];
  }

  /** The showcase's own (non-preset, language-neutral) karaoke templates for an AR, in array order. */
  private customKaraokeBrandings(
    aspectRatio: GenericTextBranding['aspectRatio'],
  ): KaraokeTextBranding[] {
    const all = this.videoCreator.organization?.karaokeTextBranding || [];
    return all.filter(
      (b): b is KaraokeTextBranding & { id: string } =>
        b.aspectRatio === aspectRatio &&
        !!b.id &&
        !b.isGlobalPreset &&
        !this.isPresetTemplate(b.id) &&
        // PLAT-11416 — a language-tagged variant must never seed another
        // language's captions; the right variant applies on top of the
        // neutral seed's family via the language-switch resolver.
        !b.language?.trim(),
    );
  }

  private customKaraokeTemplateConfig(branding: KaraokeTextBranding): {
    config: KaraokeConfig;
    templateId: string;
  } {
    const template = this.getBrandingProps(
      branding,
      'karaoke',
    ) as KaraokeTemplate;
    return {
      config: this.transformKaraokeTemplateIntoConfig(template),
      templateId: branding.id as string,
    };
  }

  /**
   * PLAT-11416 — the canonical key of the language the captions are
   * currently in (sentinels resolved to the source language, same rule as
   * the substitution engine).
   */
  private getActiveCaptionLanguageKey(): string {
    // getDefaultKaraokeConfig, not getCurrentKaraokeConfig — the latter
    // throws when captions were never produced, and this runs from render
    // paths (picker lists, the save-button label). Optional call: some
    // editor stubs/adapters expose only a slice of IVideoEditor.
    return resolveCaptionLanguageKey(
      this.videoEditor.getDefaultKaraokeConfig?.()?.language,
      this.videoCreator.rootStore?.transcriptionStore?.rawLanguage,
      this.videoCreator.story?.originalVideoLanguage,
    );
  }

  /**
   * PLAT-11416 — the language key a template save should tag, or null for a
   * plain save: null when the environment lacks the field, when the captions
   * are in the source language (the base record IS the source-language
   * face), or when the bound record is already this language's variant
   * (edit-in-place).
   */
  public getCaptionVariantKeyForSave(
    boundTemplateId: string | null,
  ): string | null {
    if (this.captionTemplateLanguagesSupported !== true) return null;
    const activeKey = this.getActiveCaptionLanguageKey();
    const sourceKey = resolveSourceLanguageKey(
      this.videoCreator.rootStore?.transcriptionStore?.rawLanguage,
      this.videoCreator.story?.originalVideoLanguage,
    );
    if (activeKey === sourceKey) return null;
    if (boundTemplateId) {
      const record = this.getKaraokeBrandingRecord(boundTemplateId);
      if (record && captionVariantMatchesLanguage(record.language, activeKey)) {
        return null;
      }
    }
    return activeKey;
  }

  /**
   * PLAT-11416 — the org's curated caption-template variant for a language,
   * as a ready-to-seed config, or null. Fed into the language switch
   * (`applyCaptionsLanguage`'s `resolveLanguageVariant`).
   */
  public getKaraokeLanguageVariantConfig(
    parentTemplateId: string,
    languageKey: string,
    aspectRatio: AspectRatio,
  ): { config: KaraokeConfig; templateId: string } | null {
    const templates = this.videoCreator.organization?.karaokeTextBranding;
    let record = findKaraokeLanguageVariant(
      templates,
      parentTemplateId,
      languageKey,
      aspectRatio,
    );
    if (!record) {
      // No variant for the target language. If the CURRENT parent is itself
      // a language variant that doesn't serve the target, route back to the
      // family's neutral base — otherwise the rebind would be one-way and
      // e.g. the Japanese variant's styling would leak into English
      // captions produced after switching back.
      const parent = templates?.find(t => t.id === parentTemplateId);
      if (
        parent?.language?.trim() &&
        !captionVariantMatchesLanguage(parent.language, languageKey)
      ) {
        record = findKaraokeNeutralFamilyMember(
          templates,
          parentTemplateId,
          aspectRatio,
        );
      }
    }
    if (!record?.id) return null;
    const template = this.getBrandingProps(
      record,
      'karaoke',
    ) as KaraokeTemplate;
    return {
      config: this.transformKaraokeTemplateIntoConfig(template),
      templateId: record.id,
    };
  }

  /**
   * PLAT-11416 — create a per-language variant record in the bound
   * template's family: the explicit-button twin of the AR-variant flow.
   * Retroactively assigns the family `presetId` with a minimal field write
   * (never the full config — the base record must keep its own styling).
   * Returns the new record's id and the family key for the in-memory stitch.
   */
  private async createKaraokeLanguageVariantInDato(
    baseTemplateId: string,
    config: KaraokeConfig,
    title: string,
    aspectRatio: AspectRatio,
    languageKey: string,
  ): Promise<{ templateId: string; presetId: string }> {
    const baseRecord = this.getKaraokeBrandingRecord(baseTemplateId);
    let presetId = baseRecord?.presetId;
    if (!presetId) {
      presetId = baseTemplateId;
      if (baseRecord && !baseRecord.isGlobalPreset) {
        await this.datoClientStore.datoClient?.items.update(baseTemplateId, {
          preset_id: presetId,
        });
        baseRecord.presetId = presetId;
      }
    }
    const templateId = await this.createKaraokeTemplateInDato(config, {
      title,
      aspectRatio,
      presetId,
      language: languageKey,
    });
    return { templateId, presetId };
  }

  /**
   * PLAT-11416 — permanently delete a workspace caption template record and
   * unlink it from the organization. Configs still pointing at the deleted
   * id degrade through the existing dangling-parent paths (warn + default).
   */
  public async deleteKaraokeTemplate(templateId: string): Promise<void> {
    const organization = this.videoCreator.organization;
    if (!organization) return;
    // Global Arbor preset records can be LINKED into a showcase's
    // karaokeTextBranding ("karaokeTextBranding mixes both" — see
    // customKaraokeBrandings); destroying one would delete it
    // for every organization.
    const record = organization.karaokeTextBranding?.find(
      b => b.id === templateId,
    );
    if (!record || record.isGlobalPreset || this.isPresetTemplate(templateId)) {
      this.logger.warn('Refusing to delete a non-workspace caption template', {
        templateId,
      });
      return;
    }
    // destory is not supported, so we just unlink the template from the organization
    // await this.datoClientStore.datoClient?.items.destroy(templateId);
    organization.karaokeTextBranding = (
      organization.karaokeTextBranding || []
    ).filter(b => b.id !== templateId);
    organization.lastUsedTemplates = (
      organization.lastUsedTemplates || []
    ).filter(t => t.id !== templateId);
    await this.datoClientStore.albumRepository?.update(organization);
    if (this.selectedTemplateId === templateId) {
      this.selectedTemplateId = GenericPresetTemplates.default;
      this.selectedBrandingTemplate = undefined;
    }
    const aspectRatio =
      this.videoCreator.currentVideo?.aspectRatio ||
      this.videoCreator.getDefaultAspectRatio();
    this.loadAllTemplates('karaoke', aspectRatio);
    this.loadLastUsedTemplate('karaoke', aspectRatio);
  }

  public createKaraokePresetProvider(): IKaraokePresetProvider {
    return new KaraokePresetProvider({
      presets: this.globalKaraokePresets,
      branding: this as any,
      userTemplates: this.videoCreator.organization?.karaokeTextBranding || [],
    });
  }

  private async applyKaraokeTemplate(
    template: KaraokeTemplate,
    newConfig: ElementState['source'] | null = null,
    createUndoRedoEntry: boolean = true,
  ): Promise<void> {
    // Skip re-application while a save is in progress — the editor already
    // has the correct config and re-applying from the template would overwrite
    // unsaved changes (e.g. transcript_maximum_length / words-per-line).
    if (this.isLoading) {
      return;
    }

    let currentKaraokeConfig = this.videoEditor.getDefaultKaraokeConfig();
    // PLAT-11689 — applying a template never shows or hides the captions.
    const newKaraokeConfig = keepCaptionsVisibility(currentKaraokeConfig, {
      ...this.transformKaraokeTemplateIntoConfig(template),
      ...newConfig,
      // PLAT-9621: preserve caption language across template apply —
      // templates carry no language, so spreading them would reset
      // the user's selected caption language back to default.
      ...(currentKaraokeConfig?.language
        ? { language: currentKaraokeConfig.language }
        : {}),
    });
    newKaraokeConfig.parent_template_id = template.id as string;
    // PLAT-11413 (epic DE6) — the template's font wins only when it can
    // render the active caption language; otherwise swap in the
    // script-equivalent, exactly like the language switch (PLAT-11409).
    // Latin languages yield no correction, so template apply there is
    // unchanged. The substitution records the TEMPLATE's font as the new
    // original (`font_before_substitution`), so a later switch to a language
    // the template's font covers restores the template's own font.
    const captionLanguageKey = resolveCaptionLanguageKey(
      newKaraokeConfig.language,
      this.videoCreator.rootStore?.transcriptionStore?.rawLanguage,
      this.videoCreator.story?.originalVideoLanguage,
    );
    const fontCorrection = getCaptionFontCorrection(
      newKaraokeConfig,
      captionLanguageKey,
    );
    if (fontCorrection) {
      Object.assign(newKaraokeConfig, fontCorrection.fields);
    }
    if (!currentKaraokeConfig) {
      currentKaraokeConfig =
        this.videoEditor.createKaraokeConfig(newKaraokeConfig);
      this.videoEditor.setDefaultKaraokeConfigId(currentKaraokeConfig.id);
    } else {
      const lineBreakChanged = captionLineBreakChanged(
        currentKaraokeConfig,
        newKaraokeConfig,
      );

      this.videoEditor.commandUpdateKaraokeConfig(
        {
          ...newKaraokeConfig,
          id: currentKaraokeConfig.id,
        },
        createUndoRedoEntry,
      );

      // Re-segment karaoke clips when the template has a different line length
      if (lineBreakChanged) {
        this.videoEditor.commandProduceKaraoke({
          replaceTrack: true,
          createUndoRedoEntry: false,
        });
      }
    }

    this.saveToExtraElementData('karaoke', template.id as string);
  }

  async applyTemplate(
    template: Template,
    type: 'basic' | 'karaoke',
    source: ElementState['source'] | null = null,
    newConfig?: ElementState['source'],
    applyPositioning: boolean = true,
    createUndoRedoEntry: boolean = true,
  ) {
    this.selectedBrandingTemplate = template;
    this.selectedTemplateId = template.id as string;

    // Reset template modification flags when applying a template
    this.prepareTemplateToSave = false;

    // Clear any existing modification data since we're applying a new template
    this.toggleModifiedTemplateData();

    if (type === 'basic') {
      await this.applyLowerThirdTemplate(
        template as LowerThirdTemplate,
        source,
        false,
        newConfig,
        applyPositioning,
      );
    } else {
      await this.applyKaraokeTemplate(
        template as KaraokeTemplate,
        newConfig,
        createUndoRedoEntry,
      );
    }
    if (
      template.id !== GenericPresetTemplates.default &&
      template.id !== this.lastUsedTemplateId
    ) {
      this.setLastUsedTemplate(
        template.id as string,
        template.title as string,
        type,
        this.videoCreator.currentVideo?.aspectRatio ||
          this.videoCreator.getDefaultAspectRatio(),
      );
      await this.datoClientStore.albumRepository?.update(
        this.videoCreator.organization!,
      );
    }
  }

  loadDefaultTemplateOnVideoLoad() {
    this.toggleModifiedTemplateData();
    const isTextSettingOpen =
      this.videoCreator.sidebarOptions === SidebarOption.design &&
      this.videoCreator.designSubMenu === DesignCard.text;

    const isKaraokeSettingOpen =
      this.videoCreator.sidebarOptions === SidebarOption.karaoke;

    if (!isKaraokeSettingOpen && !isTextSettingOpen) return;
    const type = isKaraokeSettingOpen ? 'karaoke' : 'basic';
    const activeElement = this.videoCreator.getActiveElement();
    const hasActiveElement =
      type === 'karaoke'
        ? this.videoCreator.rootStore.editorStore?.hasKaraokeTrack
        : activeElement?.source.type === 'text';
    if (!hasActiveElement) {
      this.videoCreator.sidebarOptions = SidebarOption.media;
      this.videoCreator.mediaSubMenu = MediaCard.photo;
    }

    this.getAllBrandingTemplates(
      type,
      this.videoCreator.currentVideo?.aspectRatio ||
        this.videoCreator.getDefaultAspectRatio(),
      hasActiveElement,
    );
    if (isTextSettingOpen && activeElement?.source.type === 'text') {
      this.videoCreator.textProducer.setTextConfig(activeElement.source);
    }
  }
}
